import { AccountBalance, CalendarMonth, CheckBox, FolderCopy, Groups, PeopleAlt, PermContactCalendar, Person, Settings, Tune } from '@mui/icons-material';
import SyncIcon from '@mui/icons-material/Sync';
import _ from 'lodash';
import React, { ReactElement, useMemo } from 'react';
import { ROUTE_PATHS } from '../routes/Routes';
import { Actions, useGetAllActions } from '../services/react-query/permissions';
export type MenuLinkItem = {
  label: string;
  link?: string;
  icon: ReactElement;
  children?: MenuLinkItem[];
  permission?: Actions;
};

// Define the menu links with permissions and icons as an array
export const MENU_LINKS: MenuLinkItem[] = [
  {
    label: 'Accounts',
    link: ROUTE_PATHS.ACCOUNTS,
    icon: <Person />,
    permission: Actions.ACCOUNTS_READ,
  },
  {
    label: 'Events',
    link: ROUTE_PATHS.EVENTS_TABLE,
    icon: <CalendarMonth />,
    permission: Actions.EVENTS_READ,
  },
  {
    label: 'Tasks',
    link: ROUTE_PATHS.TASKS,
    icon: <CheckBox />,
    permission: Actions.TASKS_READ,
  },
  {
    label: 'Contacts',
    link: ROUTE_PATHS.CONTACTS,
    icon: <PermContactCalendar />,
    permission: Actions.CONTACTS_READ,
  },
  {
    label: 'Employees',
    link: ROUTE_PATHS.EMPLOYEES,
    icon: <PeopleAlt />,
    permission: Actions.EMPLOYEES_READ,
  },
  {
    label: 'Setup',
    icon: <Settings />,
    children: [
      {
        label: 'Preferences',
        link: ROUTE_PATHS.PREFERENCES,
        icon: <Tune />,
        permission: Actions.PREFERENCES_WRITE,
      },
      {
        label: 'Task Templates',
        link: ROUTE_PATHS.TASK_TEMPLATES,
        icon: <FolderCopy />,
        permission: Actions.TASK_TEMPLATES_WRITE,
      },
      {
        label: 'Event Templates',
        link: ROUTE_PATHS.EVENT_TEMPLATES,
        icon: <FolderCopy />,
        permission: Actions.EVENT_TEMPLATES_WRITE,
      },
      {
        label: 'Email Templates',
        link: ROUTE_PATHS.EMAIL_TEMPLATES,
        icon: <FolderCopy />,
        permission: Actions.EMAIL_TEMPLATES_WRITE,
      },
      {
        label: 'User Permissions',
        link: ROUTE_PATHS.USER_ROLES,
        icon: <Groups />,
        permission: Actions.USER_ROLES_READ,
      },
      {
        label: 'Organization',
        link: ROUTE_PATHS.ORGANIZATION,
        icon: <AccountBalance />,
        permission: Actions.ORGANIZATION_WRITE,
      },
      {
        label: 'Integrations',
        link: ROUTE_PATHS.INTEGRATIONS,
        icon: <SyncIcon />,
        permission: Actions.INTEGRATIONS_READ,
      },
    ],
  },
];

export const useMenuItems = () => {
  // Extract unique actions from MENU_LINKS
  const actions = useMemo(() => getActionsFromMenuLinks(MENU_LINKS), []);

  // Fetch permissions based on the extracted actions
  const { data: permissions, isFetching: isFetchingPermissions } = useGetAllActions({ actions });

  // Function to filter menu items based on permissions
  const filterMenuItems = (items: MenuLinkItem[]): MenuLinkItem[] =>
    items.reduce<MenuLinkItem[]>((filteredItems, item) => {
      // Check if the item has the required permission and is not granted, then skip it
      if (item.permission && permissions && !permissions[item.permission]) {
        return filteredItems;
      }

      // If item has children, filter them recursively
      if (item.children) {
        const filteredChildren = filterMenuItems(item.children);
        if (filteredChildren.length > 0) {
          filteredItems.push({ ...item, children: filteredChildren });
        }
      } else {
        filteredItems.push(item);
      }

      return filteredItems;
    }, []);

  // Filtered menu items based on permissions
  const menuLinkItems = useMemo(() => filterMenuItems(MENU_LINKS), [permissions]);

  // Return menu items and loading state
  return { menuLinkItems, isLoading: isFetchingPermissions };
};

// Utility function to extract unique actions from the menu links
export const getActionsFromMenuLinks = (menuLinks: MenuLinkItem[]): Actions[] => {
  return _.uniq(_.flatMap(menuLinks, (item) => [item.permission, ...(item.children ? item.children.map((child) => child.permission) : [])])).filter(
    Boolean
  ) as Actions[];
};
